import isNumber from 'lodash/isNumber'

import {
  EntityView,
  ListingStructureType,
  OpportunityCancellationType,
  OpportunityView,
  TradeIntentStatusType,
} from 'graphql/bindings/AdminBindings'

export const listingStructureDisplayName = (structure: ListingStructureType): string => {
  switch (structure) {
    case ListingStructureType.DirectShareTransfer:
      return 'Direct'
    case ListingStructureType.ForwardBackedFundInterest:
      return 'Forward'
    case ListingStructureType.ShareBackedFundInterest:
      return 'SPV'
  }
}

export const opportunityStatusDisplayName = (status: TradeIntentStatusType): string => {
  switch (status) {
    case TradeIntentStatusType.Cancelled:
      return 'Cancelled'
    case TradeIntentStatusType.Closed:
      return 'Closed'
    case TradeIntentStatusType.Closings:
      return 'In Closings'
    case TradeIntentStatusType.Pipeline:
      return 'Pipeline'
    case TradeIntentStatusType.Qualified:
      return 'Qualified'
    case TradeIntentStatusType.PreClosings:
      return 'Pre-Closings'
    case TradeIntentStatusType.Settlement:
      return 'Settlement'
    case TradeIntentStatusType.Deleted:
      return 'Deleted'
    case TradeIntentStatusType.PrePipeline:
      return 'Pre Pipeline'
  }
}

export const opportunityCancellationTypeDisplayName = (cancellationType: OpportunityCancellationType): string => {
  switch (cancellationType) {
    case OpportunityCancellationType.NoIntent:
      return 'Not serious / Unresponsive / Duplicate'
    case OpportunityCancellationType.FellThrough:
      return 'Opportunity fell through'
    case OpportunityCancellationType.LostToCompetitor:
      return 'Transacted elsewhere'
    case OpportunityCancellationType.Closed:
      return 'Closed with Forge'
  }
}

export const isNotCancelledOrClosed = (opp: { status: TradeIntentStatusType }): boolean =>
  ![TradeIntentStatusType.Cancelled, TradeIntentStatusType.Deleted, TradeIntentStatusType.Closed].includes(opp.status)

export enum DealBlockerType {
  SizeMissing = 'Specify either total volume or or number of shares',
  PriceMissing = 'Specify either share price or valuation',
  FundingRoundMissing = 'Share price is unable to be determined without previous funding round',
  EntityMissing = 'Select an entity for the trade intent',
  NotPrePipeline = 'Only pre-pipeline opportunities can become deals',
}

const isBlocker = (val: any): val is DealBlockerType => {
  return Object.values(DealBlockerType).includes(val)
}

const dealBlockers = ({
  accountEntityCount,
  outstandingShares,
  pricingType,
  minDollarAmount,
  dollarAmount,
  minNumShares,
  numShares,
  sharePrice,
  valuation,
  hasEntity,
  status,
}: {
  accountEntityCount?: number
  outstandingShares: number
  pricingType?: string
  minDollarAmount: number
  dollarAmount: number
  minNumShares: number
  numShares: number
  sharePrice: number
  valuation: number
  hasEntity: boolean
  status: TradeIntentStatusType
}): DealBlockerType[] => {
  const needsFundingRound = pricingType === 'valuation' ? !outstandingShares : false
  const needsEntitySelection = !hasEntity && isNumber(accountEntityCount) && accountEntityCount > 1

  return [
    !(minDollarAmount || dollarAmount || minNumShares || numShares) && DealBlockerType.SizeMissing,
    !(sharePrice || valuation) && DealBlockerType.PriceMissing,
    needsFundingRound && DealBlockerType.FundingRoundMissing,
    needsEntitySelection && DealBlockerType.EntityMissing,
    status !== TradeIntentStatusType.PrePipeline && DealBlockerType.NotPrePipeline,
  ].filter(isBlocker)
}

export const dealBlockersForOpportunity = ({
  account,
  company,
  status,
  pricing_type,
  min_dollar_amount,
  dollar_amount,
  min_num_shares,
  num_shares,
  share_price,
  valuation,
  entity_id,
}: {
  account?: {
    __typename?: 'AccountView'
    entities?: Pick<EntityView, 'id'>[]
  }
  company: Pick<OpportunityView['company'], 'last_round_estimated_number_of_shares'>
} & Pick<
  OpportunityView,
  | 'status'
  | 'entity_id'
  | 'pricing_type'
  | 'min_dollar_amount'
  | 'dollar_amount'
  | 'min_num_shares'
  | 'num_shares'
  | 'share_price'
  | 'valuation'
>) => {
  return dealBlockers({
    accountEntityCount: account?.entities?.length,
    outstandingShares: company.last_round_estimated_number_of_shares,
    pricingType: pricing_type ?? undefined,
    minDollarAmount: min_dollar_amount,
    dollarAmount: dollar_amount,
    minNumShares: min_num_shares,
    numShares: num_shares,
    sharePrice: share_price,
    valuation: valuation,
    hasEntity: !!entity_id,
    status,
  })
}
